import React, { useState, useEffect } from "react";
import { makeRequest2 } from "../../../../../utils/makeRequest";
import { getItem } from "../../../../../utils/localStorage";
import { toastify } from "../../../../../utils/toast";
import { useFacilityStore } from "../../../../../app/z-store/store";
import WalletTransactionsModal from "./wallet_transactions_modal";
import WalletMetadataModal from "./wallet_metadata_modal";
import TransferMoneyModal from "./transfer_money_modal";
import CreateFacilityWalletModal from "./create_facility_wallet_modal";
import PendingApprovalsModal from "./pending_approvals_modal";

const WalletTab = () => {
  // State management
  const [loading, setLoading] = useState(false);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [facilityWallets, setFacilityWallets] = useState([]);

  // Modal states
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showCreateWalletModal, setShowCreateWalletModal] = useState(false);
  const [showPendingApprovalsModal, setShowPendingApprovalsModal] =
    useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  // Stats
  const [walletStats, setWalletStats] = useState({
    totalWallets: 0,
    activeWallets: 0,
    totalBalance: 0,
    avgBalance: 0,
    facilityWallets: 0,
    userWallets: 0,
    customerWallets: 0,
  });

  // Enhanced approval stats with detailed metadata tracking
  const [approvalStats, setApprovalStats] = useState({
    pendingCount: 0,
    pendingAmount: 0,
    pendingMetadata: [], // Store actual pending metadata items
    pendingWallets: [], // Store wallets with pending metadata
    lastUpdated: null,
  });

  // Filter states
  const [walletFilters, setWalletFilters] = useState({
    ownerType: "",
    walletType: "",
    isActive: true,
    page: 1,
    limit: 10,
  });

  // Get facility ID from Zustand store
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  // Available wallet types with full configuration
  const walletTypes = [
    { value: "", label: "All Types", color: "secondary" },
    {
      value: "levy",
      label: "Levy",
      color: "primary",
      description: "Levy payments and collections",
    },
    {
      value: "propertyManagement",
      label: "Property Management",
      color: "info",
      description: "Management fees and expenses",
    },
    {
      value: "lease",
      label: "Lease",
      color: "success",
      description: "Rental transactions",
    },
    {
      value: "utility",
      label: "Utility",
      color: "warning",
      description: "Utility bills and payments",
    },
    {
      value: "vas",
      label: "Value Added Services",
      color: "secondary",
      description: "Additional services and amenities",
    },
  ];

  // Enhanced pending approvals loading with proper metadata extraction
  const loadApprovalStats = async (walletsToUse = null) => {
    if (!selectedFacilityId) return;

    try {
      console.log(
        "🔄 Loading approval stats for facility:",
        selectedFacilityId,
      );

      // Use provided wallets or fall back to state
      const walletsToCheck = walletsToUse
        ? walletsToUse.filter((wallet) => wallet.ownerType === "Facility")
        : facilityWallets.filter((wallet) => wallet.ownerType === "Facility");

      console.log("📊 Using facility wallets:", walletsToCheck.length);

      // If no wallets to check, try to load them first
      if (walletsToCheck.length === 0 && !walletsToUse) {
        console.log("🔄 No facility wallets found, loading wallets first...");
        const result = await loadFacilityWallets(1, walletFilters);
        if (result && result.wallets) {
          const facilityWalletsFromAPI = result.wallets.filter(
            (wallet) => wallet.ownerType === "Facility",
          );
          console.log(
            "📊 Loaded facility wallets from API:",
            facilityWalletsFromAPI.length,
          );
          return await loadApprovalStats(facilityWalletsFromAPI);
        }
      }

      let totalPendingCount = 0;
      let totalPendingAmount = 0;
      let allPendingMetadata = [];
      let walletsWithPending = [];

      // Check each facility wallet for pending metadata
      for (const wallet of walletsToCheck) {
        try {
          console.log(
            `🔍 Checking metadata for wallet: ${wallet._id} (${wallet.walletType})`,
          );

          const metadataResponse = await makeRequest2(
            `/api/app/wallet/${selectedFacilityId}/wallet/${wallet._id}/metadata`,
            "GET",
          );

          console.log(
            `📥 Metadata response for wallet ${wallet._id}:`,
            metadataResponse,
          );

          let metadata = [];

          // Extract metadata from response
          if (
            metadataResponse.message ===
              "Wallet metadata retrieved successfully" &&
            metadataResponse.metadata
          ) {
            metadata = metadataResponse.metadata || [];
          } else if (metadataResponse.data && metadataResponse.data.metadata) {
            metadata = metadataResponse.data.metadata || [];
          } else if (metadataResponse.metadata) {
            metadata = metadataResponse.metadata || [];
          }

          // Filter for pending status metadata
          const pendingItems = metadata.filter((item) => {
            const isPending = item.status === "pending";
            console.log(
              `🔍 Item ${item._id}: status=${item.status}, isPending=${isPending}`,
            );
            return isPending;
          });

          console.log(
            `✅ Found ${pendingItems.length} pending items in wallet ${wallet._id}`,
          );

          if (pendingItems.length > 0) {
            // Add wallet information to each pending metadata item
            const enrichedMetadata = pendingItems.map((item) => ({
              ...item,
              walletId: wallet._id,
              walletType: wallet.walletType,
              walletOwner: wallet.owner,
              walletAmount: wallet.amount,
            }));

            allPendingMetadata.push(...enrichedMetadata);
            totalPendingCount += pendingItems.length;
            totalPendingAmount += pendingItems.reduce(
              (sum, item) => sum + (item.amount || 0),
              0,
            );

            // Track wallets that have pending metadata
            walletsWithPending.push({
              ...wallet,
              pendingCount: pendingItems.length,
              pendingAmount: pendingItems.reduce(
                (sum, item) => sum + (item.amount || 0),
                0,
              ),
              pendingItems: enrichedMetadata,
            });
          }
        } catch (error) {
          console.error(
            `❌ Error loading metadata for wallet ${wallet._id}:`,
            error,
          );
          // Continue with other wallets
        }
      }

      console.log("📊 Final approval stats:", {
        totalPendingCount,
        totalPendingAmount,
        metadataItems: allPendingMetadata.length,
        walletsWithPending: walletsWithPending.length,
      });

      setApprovalStats({
        pendingCount: totalPendingCount,
        pendingAmount: totalPendingAmount,
        pendingMetadata: allPendingMetadata,
        pendingWallets: walletsWithPending,
        lastUpdated: new Date().toISOString(),
      });
    } catch (error) {
      console.error("❌ Error loading approval stats:", error);
      // Reset stats on error
      setApprovalStats({
        pendingCount: 0,
        pendingAmount: 0,
        pendingMetadata: [],
        pendingWallets: [],
        lastUpdated: null,
      });
    }
  };
  const getRelativeTime = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  // Load all facility wallets
  const loadFacilityWallets = async (page = 1, filters = {}) => {
    try {
      setLoading(true);

      if (!selectedFacilityId) {
        toastify("Facility ID not found. Please select a facility.", "error");
        return;
      }

      const queryParams = new URLSearchParams();
      queryParams.append("page", page);
      queryParams.append("limit", filters.limit || 10);

      if (filters.ownerType) {
        queryParams.append("ownerType", filters.ownerType);
      }

      if (filters.walletType) {
        queryParams.append("walletType", filters.walletType);
      }

      if (filters.isActive !== undefined) {
        queryParams.append("includeInactive", !filters.isActive);
      }

      const walletsUrl = `/api/app/wallet/${selectedFacilityId}/all?${queryParams.toString()}`;
      console.log("📡 Calling facility wallets API:", walletsUrl);

      const response = await makeRequest2(walletsUrl, "GET");
      console.log("📥 Facility wallets API response:", response);

      let walletsData = [];
      let paginationData = { total: 0, page: 1, limit: 10, pages: 1 };

      if (response.success && response.data) {
        walletsData = response.data.wallets || [];
        paginationData = response.data.pagination || paginationData;
      } else if (response.message && response.wallets) {
        walletsData = response.wallets;
        paginationData = response.pagination || paginationData;
      }

      setFacilityWallets(walletsData);
      calculateWalletStats(walletsData);

      // Load approval stats immediately with fresh wallet data
      if (walletsData.length > 0) {
        console.log("🔄 Loading approval stats with fresh wallet data");
        await loadApprovalStats(walletsData);
      }

      return { wallets: walletsData, pagination: paginationData };
    } catch (error) {
      console.error("❌ Error loading facility wallets:", error);
      toastify("Error loading facility wallets", "error");
      return {
        wallets: [],
        pagination: { total: 0, page: 1, limit: 10, pages: 1 },
      };
    } finally {
      setLoading(false);
    }
  };

  // Calculate wallet statistics
  const calculateWalletStats = (wallets) => {
    if (!wallets || wallets.length === 0) {
      setWalletStats({
        totalWallets: 0,
        activeWallets: 0,
        totalBalance: 0,
        avgBalance: 0,
        facilityWallets: 0,
        userWallets: 0,
        customerWallets: 0,
      });
      return;
    }

    const activeWallets = wallets.filter((w) => w.isActive);
    const totalBalance = wallets.reduce(
      (sum, wallet) => sum + (wallet.amount || 0),
      0,
    );
    const facilityWallets = wallets.filter((w) => w.ownerType === "Facility");
    const userWallets = wallets.filter((w) => w.ownerType === "User");
    const customerWallets = wallets.filter((w) => w.ownerType === "Customer");

    setWalletStats({
      totalWallets: wallets.length,
      activeWallets: activeWallets.length,
      totalBalance: totalBalance,
      avgBalance: wallets.length > 0 ? totalBalance / wallets.length : 0,
      facilityWallets: facilityWallets.length,
      userWallets: userWallets.length,
      customerWallets: customerWallets.length,
    });
  };

  // Load wallet transactions
  const loadWalletTransactions = async (walletId, page = 1) => {
    try {
      setTransactionsLoading(true);

      if (!selectedFacilityId || !walletId) {
        toastify("Missing required parameters", "error");
        return;
      }

      const walletIdStr = String(walletId).trim();
      const transactionsUrl = `/api/app/wallet/${selectedFacilityId}/transactions/wallet/${walletIdStr}?page=${page}&limit=50`;
      console.log("📡 Calling transactions API:", transactionsUrl);

      const response = await makeRequest2(transactionsUrl, "GET");
      console.log("📥 Transactions API response:", response);

      if (response.success || response.data || response.transactions) {
        const transactionsData =
          response.data?.transactions || response.transactions || [];
        const safeTransactionsData = Array.isArray(transactionsData)
          ? transactionsData
          : [];

        const processedTransactions = safeTransactionsData.map(
          (transaction) => ({
            ...transaction,
            transactionType:
              transaction.type ||
              transaction.transactionType ||
              (transaction.amount > 0 ? "topup" : "deductable"),
            amount: Math.abs(transaction.amount || 0),
            createdAt:
              transaction.createdAt ||
              transaction.date ||
              new Date().toISOString(),
            description:
              transaction.description ||
              transaction.purpose ||
              transaction.reason ||
              "N/A",
          }),
        );

        setTransactions(processedTransactions);
        console.log(
          "✅ Transactions loaded:",
          processedTransactions.length,
          "transactions",
        );
        return response;
      } else {
        setTransactions([]);
        console.log("❌ No transactions found or error:", response);
        if (response.error && !response.error.includes("not found")) {
          toastify(response.error || "Failed to load transactions", "error");
        }
      }
    } catch (error) {
      console.error("❌ Error loading transactions:", error);
      setTransactions([]);
      toastify("Error loading transactions", "error");
    } finally {
      setTransactionsLoading(false);
    }
  };

  // Handle wallet transfers
  const handleTransferMoney = async (transferData) => {
    try {
      setLoading(true);

      if (!selectedFacilityId) {
        toastify("Facility ID not found. Please select a facility.", "error");
        return;
      }

      const transferUrl = `/api/app/wallet/${selectedFacilityId}/transfer`;
      console.log("📡 Calling transfer API:", transferUrl, transferData);

      const response = await makeRequest2(transferUrl, "PUT", transferData);
      console.log("📥 Transfer API response:", response);

      if (
        response.success ||
        response.message === "Money transferred successfully"
      ) {
        toastify("Money transferred successfully", "success");
        await loadFacilityWallets(1, walletFilters);
        setShowTransferModal(false);
        return true;
      } else {
        toastify(response.error || "Transfer failed", "error");
        return false;
      }
    } catch (error) {
      console.error("❌ Error transferring money:", error);
      toastify(error.message || "Error transferring money", "error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Handle create facility wallet success
  const handleCreateWalletSuccess = () => {
    loadFacilityWallets(1, walletFilters);
  };

  // Enhanced approval success handler
  const handleApprovalSuccess = async () => {
    console.log("🔄 Handling approval success - refreshing data");
    await loadFacilityWallets(1, walletFilters); // This will also refresh approval stats
    toastify("Approval processed successfully", "success");
  };

  // Load initial data
  useEffect(() => {
    if (selectedFacilityId) {
      console.log("🚀 Loading initial data for facility:", selectedFacilityId);
      loadFacilityWallets(1, walletFilters);
    }
  }, [selectedFacilityId]);

  // Update the periodic refresh useEffect to also depend on facilityWallets:
  useEffect(() => {
    if (selectedFacilityId && facilityWallets.length > 0) {
      const interval = setInterval(() => {
        console.log("⏰ Periodic refresh of approval stats");
        loadApprovalStats();
      }, 300000);

      return () => clearInterval(interval);
    }
  }, [selectedFacilityId, facilityWallets.length]);

  // Handle view transactions
  const handleViewTransactions = (wallet) => {
    if (!wallet) {
      console.error("No wallet provided");
      toastify("Cannot view transactions: No wallet available", "error");
      return;
    }

    if (!wallet._id) {
      console.error("Wallet has no ID property", wallet);
      toastify("Cannot view transactions: Wallet ID missing", "error");
      return;
    }

    console.log("Loading transactions for wallet:", wallet._id);
    setSelectedWallet(wallet);
    loadWalletTransactions(wallet._id)
      .then(() => {
        setShowTransactionsModal(true);
      })
      .catch((error) => {
        console.error("Failed to load transactions:", error);
        toastify("Error loading transactions", "error");
      });
  };

  // Handle view metadata
  const handleViewMetadata = (wallet) => {
    if (!wallet) {
      console.error("No wallet provided");
      toastify("Cannot view metadata: No wallet available", "error");
      return;
    }

    if (wallet.ownerType !== "Facility") {
      toastify("Metadata is only available for facility wallets", "info");
      return;
    }

    setSelectedWallet(wallet);
    setShowMetadataModal(true);
  };

  // Enhanced pending approvals handler
  const handleViewPendingApprovals = () => {
    console.log("📋 Opening pending approvals modal");
    console.log(
      "Pending metadata count:",
      approvalStats.pendingMetadata.length,
    );
    console.log("Pending wallets:", approvalStats.pendingWallets.length);
    setShowPendingApprovalsModal(true);
  };

  // Check if wallet has pending metadata
  const walletHasPendingMetadata = (walletId) => {
    return approvalStats.pendingWallets.some(
      (wallet) => wallet._id === walletId,
    );
  };

  // Get pending count for specific wallet
  const getWalletPendingCount = (walletId) => {
    const wallet = approvalStats.pendingWallets.find((w) => w._id === walletId);
    return wallet ? wallet.pendingCount : 0;
  };

  // Utility functions
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  // Fixed formatDate function with proper error handling
  const formatDate = (dateString) => {
    // Handle null, undefined, or empty values
    if (!dateString) {
      return "N/A";
    }

    // Create date object
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn("Invalid date string:", dateString);
      return "Invalid Date";
    }

    // Return formatted date
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get wallet type configuration
  const getWalletTypeConfig = (walletType) => {
    return (
      walletTypes.find((type) => type.value === walletType) || walletTypes[0]
    );
  };

  // Get wallet type display name
  const getWalletTypeDisplayName = (walletType) => {
    const typeConfig = getWalletTypeConfig(walletType);
    return typeConfig.label;
  };

  // Handle refresh data
  const handleRefreshData = async () => {
    console.log("🔄 Manual refresh triggered");
    await loadFacilityWallets(1, walletFilters); // This will also refresh approval stats
  };

  // Handle reset filters
  const handleResetFilters = () => {
    const defaultFilters = {
      ownerType: "",
      walletType: "",
      isActive: true,
      page: 1,
      limit: 10,
    };
    setWalletFilters(defaultFilters);
    loadFacilityWallets(1, defaultFilters);
  };

  return (
    <div className="wallet-management">
      {/* Header with Pending Approvals Button */}
      <div className="row mb-4">
        <div className="col-md-12">
          <div className="d-flex justify-content-between align-items-center">
            <h4>
              <i className="fa fa-wallet mr-2"></i>
              Facility Wallets Management
            </h4>
            {/* Enhanced Pending Approvals Button */}
            <div className="btn-group" role="group">
              {/* Main Pending Approvals Button */}
              <button
                className="btn btn-warning position-relative"
                onClick={handleViewPendingApprovals}
                title={`View ${approvalStats.pendingCount} Pending Approvals (${formatCurrency(approvalStats.pendingAmount)})`}
                style={{ position: "relative" }}
              >
                <i className="fa fa-clock mr-2"></i>
                Pending Approvals
                {approvalStats.pendingCount > 0 && (
                  <span
                    className="badge badge-danger"
                    style={{
                      position: "absolute",
                      top: "-8px",
                      right: "-8px",
                      borderRadius: "50%",
                      padding: "4px 6px",
                      fontSize: "10px",
                      minWidth: "18px",
                      height: "18px",
                      lineHeight: "10px",
                    }}
                  >
                    {approvalStats.pendingCount > 99
                      ? "99+"
                      : approvalStats.pendingCount}
                  </span>
                )}
              </button>

              {/* Last Updated as Button-Badge */}
              {approvalStats.lastUpdated && (
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled
                  style={{
                    cursor: "default",
                    fontSize: "11px",
                    padding: "8px 12px",
                    border: "1px solid #6c757d",
                    backgroundColor: "#f8f9fa",
                    color: "#6c757d",
                  }}
                  title={`Last updated: ${formatDate(approvalStats.lastUpdated)}`}
                >
                  <i className="fa fa-sync mr-1"></i>
                  {getRelativeTime(approvalStats.lastUpdated)}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Statistics Cards with Approval Stats */}
      <div className="row mb-4">
        <div className="col-md-2 d-flex">
          <div className="card border-primary flex-fill">
            <div className="card-body text-center d-flex flex-column justify-content-center">
              <h6 className="text-muted">Total Wallets</h6>
              <h3 className="text-primary">{walletStats.totalWallets}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-2 d-flex">
          <div className="card border-success flex-fill">
            <div className="card-body text-center d-flex flex-column justify-content-center">
              <h6 className="text-muted">Active</h6>
              <h3 className="text-success">{walletStats.activeWallets}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-2 d-flex">
          <div className="card border-info flex-fill">
            <div className="card-body text-center d-flex flex-column justify-content-center">
              <h6 className="text-muted">Total Balance</h6>
              <h5 className="text-info">
                {formatCurrency(walletStats.totalBalance)}
              </h5>
            </div>
          </div>
        </div>
        {/* Enhanced Pending Approvals Card */}
        <div className="col-md-2 d-flex">
          <div
            className="card border-warning flex-fill"
            style={{ cursor: "pointer" }}
            onClick={handleViewPendingApprovals}
            title="Click to view pending approvals"
          >
            <div className="card-body text-center d-flex flex-column justify-content-center">
              <h6 className="text-muted">Pending Approvals</h6>
              <h3 className="text-warning">{approvalStats.pendingCount}</h3>
              <small className="text-muted">
                {formatCurrency(approvalStats.pendingAmount)}
              </small>
              {approvalStats.pendingCount > 0 && (
                <div className="mt-1">
                  <small className="badge badge-warning">
                    {approvalStats.pendingWallets.length} wallets
                  </small>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-2 d-flex">
          <div className="card border-secondary flex-fill">
            <div className="card-body text-center d-flex flex-column justify-content-center">
              <h6 className="text-muted">Users</h6>
              <h3 className="text-secondary">{walletStats.userWallets}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-2 d-flex">
          <div className="card border-dark flex-fill">
            <div className="card-body text-center d-flex flex-column justify-content-center">
              <h6 className="text-muted">Customers</h6>
              <h3 className="text-dark">{walletStats.customerWallets}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Filters with Create Button */}
      <div className="card mb-4">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="card-title mb-0">Filter Wallets</h6>
            <div className="btn-group">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={handleResetFilters}
                disabled={loading}
              >
                <i className="fa fa-undo mr-1"></i>
                Reset
              </button>
              <button
                className="btn btn-success btn-sm"
                onClick={() => setShowCreateWalletModal(true)}
              >
                <i className="fa fa-plus mr-1"></i>
                Create Facility Wallet
              </button>
            </div>
          </div>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-3">
              <div className="form-group">
                <label>Owner Type</label>
                <select
                  className="form-control"
                  value={walletFilters.ownerType}
                  onChange={(e) =>
                    setWalletFilters({
                      ...walletFilters,
                      ownerType: e.target.value,
                    })
                  }
                >
                  <option value="">All Types</option>
                  <option value="User">User</option>
                  <option value="Customer">Customer</option>
                  <option value="Facility">Facility</option>
                </select>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label>Wallet Type</label>
                <select
                  className="form-control"
                  value={walletFilters.walletType}
                  onChange={(e) =>
                    setWalletFilters({
                      ...walletFilters,
                      walletType: e.target.value,
                    })
                  }
                >
                  {walletTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label>Status</label>
                <select
                  className="form-control"
                  value={walletFilters.isActive.toString()}
                  onChange={(e) =>
                    setWalletFilters({
                      ...walletFilters,
                      isActive: e.target.value === "true",
                    })
                  }
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                  <option value="">All</option>
                </select>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label>&nbsp;</label>
                <button
                  className="btn btn-primary btn-block"
                  onClick={() => loadFacilityWallets(1, walletFilters)}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2"></span>
                      Loading...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-filter mr-1"></i>
                      Apply Filters
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Wallets Table */}
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="card-title mb-0">All Wallets</h6>
            <div className="btn-group">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={handleRefreshData}
                disabled={loading}
              >
                <i className="fa fa-sync mr-1"></i>
                Refresh
              </button>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => loadFacilityWallets(1, walletFilters)}
                disabled={loading}
              >
                <i className="fa fa-search mr-1"></i>
                Search
              </button>
            </div>
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-2">Loading wallets...</p>
            </div>
          ) : facilityWallets.length === 0 ? (
            <div className="text-center py-4">
              <i className="fa fa-wallet fa-3x text-muted mb-3"></i>
              <p className="text-muted">No wallets found</p>
              <p className="text-sm text-muted">
                Try adjusting your filters or create a new facility wallet
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>Owner Type</th>
                    <th>Wallet Type</th>
                    <th>Owner ID</th>
                    <th>Balance</th>
                    <th>Status</th>
                    <th>Pending</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {facilityWallets.map((walletItem, index) => (
                    <tr
                      key={walletItem._id}
                      className={
                        walletHasPendingMetadata(walletItem._id)
                          ? "table-warning"
                          : ""
                      }
                    >
                      <td>
                        <span
                          className="font-weight-bold"
                          style={{
                            backgroundColor:
                              walletItem.ownerType === "User"
                                ? "#e3f2fd"
                                : walletItem.ownerType === "Customer"
                                  ? "#e0f2f1"
                                  : "#fff3e0",
                            color:
                              walletItem.ownerType === "User"
                                ? "#1976d2"
                                : walletItem.ownerType === "Customer"
                                  ? "#388e3c"
                                  : "#f57c00",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                          }}
                        >
                          {walletItem.ownerType}
                        </span>
                      </td>
                      <td>
                        {walletItem.walletType ? (
                          <span
                            className="font-weight-bold"
                            style={{
                              backgroundColor:
                                getWalletTypeConfig(walletItem.walletType)
                                  .color === "primary"
                                  ? "#e3f2fd"
                                  : getWalletTypeConfig(walletItem.walletType)
                                        .color === "info"
                                    ? "#e0f7fa"
                                    : getWalletTypeConfig(walletItem.walletType)
                                          .color === "success"
                                      ? "#e8f5e8"
                                      : getWalletTypeConfig(
                                            walletItem.walletType,
                                          ).color === "warning"
                                        ? "#fff3e0"
                                        : "#f5f5f5",
                              color:
                                getWalletTypeConfig(walletItem.walletType)
                                  .color === "primary"
                                  ? "#1976d2"
                                  : getWalletTypeConfig(walletItem.walletType)
                                        .color === "info"
                                    ? "#0097a7"
                                    : getWalletTypeConfig(walletItem.walletType)
                                          .color === "success"
                                      ? "#388e3c"
                                      : getWalletTypeConfig(
                                            walletItem.walletType,
                                          ).color === "warning"
                                        ? "#f57c00"
                                        : "#757575",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                            }}
                            title={
                              getWalletTypeConfig(walletItem.walletType)
                                .description
                            }
                          >
                            {getWalletTypeDisplayName(walletItem.walletType)}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="font-monospace text-sm">
                        {walletItem.owner}
                      </td>
                      <td>
                        <strong className="text-success">
                          {formatCurrency(walletItem.amount)}
                        </strong>
                      </td>
                      <td>
                        <span
                          className="font-weight-bold"
                          style={{
                            backgroundColor: walletItem.isActive
                              ? "#d4edda"
                              : "#f8d7da",
                            color: walletItem.isActive ? "#155724" : "#721c24",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                          }}
                        >
                          {walletItem.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        {walletItem.ownerType === "Facility" &&
                        walletHasPendingMetadata(walletItem._id) ? (
                          <span
                            className="badge badge-warning"
                            title={`${getWalletPendingCount(walletItem._id)} pending approval(s)`}
                          >
                            <i className="fa fa-clock mr-1"></i>
                            {getWalletPendingCount(walletItem._id)}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-sm">
                        {formatDate(walletItem.createdAt)}
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => handleViewTransactions(walletItem)}
                            title="View Transactions"
                          >
                            <i className="fa fa-list"></i>
                          </button>
                          {walletItem.ownerType === "Facility" && (
                            <button
                              className={`btn ${walletHasPendingMetadata(walletItem._id) ? "btn-warning" : "btn-outline-info"}`}
                              onClick={() => handleViewMetadata(walletItem)}
                              title={`View Metadata${walletHasPendingMetadata(walletItem._id) ? " (Has Pending Items)" : ""}`}
                            >
                              <i className="fa fa-file-invoice-dollar"></i>
                              {walletHasPendingMetadata(walletItem._id) && (
                                <span className="badge badge-light ml-1">
                                  {getWalletPendingCount(walletItem._id)}
                                </span>
                              )}
                            </button>
                          )}
                          <button
                            className="btn btn-outline-success"
                            onClick={() => {
                              setSelectedWallet(walletItem);
                              setShowTransferModal(true);
                            }}
                            disabled={
                              !walletItem.isActive || walletItem.amount <= 0
                            }
                            title="Transfer Money"
                          >
                            <i className="fa fa-exchange-alt"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* All Modals */}
      <WalletTransactionsModal
        show={showTransactionsModal}
        onClose={() => setShowTransactionsModal(false)}
        wallet={selectedWallet}
        transactions={Array.isArray(transactions) ? transactions : []}
        onLoadMore={(page) => loadWalletTransactions(selectedWallet?._id, page)}
        loading={transactionsLoading}
      />

      <WalletMetadataModal
        show={showMetadataModal}
        onClose={() => setShowMetadataModal(false)}
        wallet={selectedWallet}
        facilityId={selectedFacilityId}
        onRefresh={loadApprovalStats} // Pass refresh function to update pending counts
      />

      <TransferMoneyModal
        show={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        onSubmit={handleTransferMoney}
        sourceWallet={selectedWallet}
        wallets={facilityWallets}
        loading={loading}
      />

      <CreateFacilityWalletModal
        show={showCreateWalletModal}
        onClose={() => setShowCreateWalletModal(false)}
        onSuccess={handleCreateWalletSuccess}
        facilityId={selectedFacilityId}
      />

      {/* Enhanced Pending Approvals Modal */}
      <PendingApprovalsModal
        show={showPendingApprovalsModal}
        onClose={() => setShowPendingApprovalsModal(false)}
        facilityId={selectedFacilityId}
        onApprovalSuccess={handleApprovalSuccess}
        pendingMetadata={approvalStats.pendingMetadata}
        pendingWallets={approvalStats.pendingWallets}
        totalPendingAmount={approvalStats.pendingAmount}
      />
    </div>
  );
};

export default WalletTab;
